import React, { useEffect, useState, useCallback } from "react";
import { Input, Select, Badge, Spin, Empty, Avatar, Modal } from "antd";
import { SearchOutlined, MailOutlined, CloseOutlined, EyeOutlined, TeamOutlined, CheckCircleOutlined } from "@ant-design/icons";
import axios from '../../services/axios';

const { Option } = Select;

const USER_TYPE_CONFIG = {
  Consultant: { color: "blue",   bg: "#E6F1FB", text: "#185FA5", accent: "#2d7dd2" },
  Contractor: { color: "green",  bg: "#EAF3DE", text: "#3B6D11", accent: "#52a529" },
  Client:     { color: "purple", bg: "#EEEDFE", text: "#534AB7", accent: "#6c63d4" },
  default:    { color: "gold",   bg: "#FAEEDA", text: "#854F0B", accent: "#d4922a" },
};

const TYPE_ICONS = {
  Consultant: "💼",
  Contractor: "🔧",
  Client:     "🏢",
  "Super User": "⭐",
};

function getTypeConfig(userType = "") {
  return USER_TYPE_CONFIG[userType] || USER_TYPE_CONFIG.default;
}

function getInitials(firstName = "", lastName = "") {
  return `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();
}

function StatCard({ label, value, icon, colorClass, badge }) {
  const colorMap = {
    total:      { bg: "#F1EFE8", stroke: "#5F5E5A", labelColor: "#888780" },
    active:     { bg: "#EAF3DE", stroke: "#3B6D11", labelColor: "#3B6D11", badgeBg: "#EAF3DE", badgeText: "#3B6D11" },
    inactive:   { bg: "#F1EFE8", stroke: "#888780", labelColor: "#888780", badgeBg: "#F1EFE8", badgeText: "#5F5E5A" },
    Consultant: { bg: "#E6F1FB", stroke: "#185FA5", labelColor: "#185FA5", badgeBg: "#E6F1FB", badgeText: "#185FA5" },
    Contractor: { bg: "#EAF3DE", stroke: "#3B6D11", labelColor: "#3B6D11", badgeBg: "#EAF3DE", badgeText: "#3B6D11" },
    Client:     { bg: "#EEEDFE", stroke: "#534AB7", labelColor: "#534AB7", badgeBg: "#EEEDFE", badgeText: "#534AB7" },
    "Super User":{ bg: "#FAEEDA", stroke: "#854F0B", labelColor: "#854F0B", badgeBg: "#FAEEDA", badgeText: "#854F0B" },
  };

  const cfg = colorMap[colorClass] || colorMap.total;
  const isMain = colorClass === "total";

  const ICONS = {
    total: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="6" cy="5" r="2.5" stroke={cfg.stroke} strokeWidth="1.2"/>
        <circle cx="10.5" cy="5" r="2.5" stroke={cfg.stroke} strokeWidth="1.2"/>
        <path d="M1 13c0-2.2 2.2-4 5-4" stroke={cfg.stroke} strokeWidth="1.2" strokeLinecap="round"/>
        <path d="M7 13c0-2.2 1.6-4 3.5-4S14 10.8 14 13" stroke={cfg.stroke} strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    ),
    active: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="6.5" stroke={cfg.stroke} strokeWidth="1.2"/>
        <path d="M5 8l2 2 4-4" stroke={cfg.stroke} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    inactive: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="6.5" stroke={cfg.stroke} strokeWidth="1.2"/>
        <path d="M6 6l4 4M10 6l-4 4" stroke={cfg.stroke} strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    Consultant: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect x="2" y="9" width="4" height="5" rx="1" stroke={cfg.stroke} strokeWidth="1.2"/>
        <rect x="6.5" y="6" width="4" height="8" rx="1" stroke={cfg.stroke} strokeWidth="1.2"/>
        <rect x="11" y="3" width="4" height="11" rx="1" stroke={cfg.stroke} strokeWidth="1.2"/>
      </svg>
    ),
    Contractor: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M3 13V7l5-4 5 4v6" stroke={cfg.stroke} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="6" y="9" width="4" height="4" rx="0.5" stroke={cfg.stroke} strokeWidth="1.2"/>
      </svg>
    ),
    Client: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect x="2" y="4" width="12" height="9" rx="1.5" stroke={cfg.stroke} strokeWidth="1.2"/>
        <path d="M5 4V3a3 3 0 016 0v1" stroke={cfg.stroke} strokeWidth="1.2" strokeLinecap="round"/>
        <circle cx="8" cy="8.5" r="1.5" fill={cfg.stroke}/>
      </svg>
    ),
    "Super User": (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M8 2l1.5 3.5L13 6l-2.5 2.5.5 3.5L8 10.5 5 12l.5-3.5L3 6l3.5-.5z" stroke={cfg.stroke} strokeWidth="1.2" strokeLinejoin="round"/>
      </svg>
    ),
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        background: isMain ? "#F8F8F6" : "#fff",
        border: `0.5px solid ${isMain ? "#D3D1C7" : "#E8E8E8"}`,
        borderRadius: 12,
        padding: "12px 16px",
        minWidth: 110,
        flex: 1,
        transition: "border-color 0.15s, box-shadow 0.15s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#bbb";
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = isMain ? "#D3D1C7" : "#E8E8E8";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Icon bubble */}
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 8,
          background: cfg.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {ICONS[colorClass] || ICONS.total}
      </div>

      {/* Text */}
      <div>
        <p
          style={{
            fontSize: 11,
            color: cfg.labelColor,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            fontWeight: 500,
            margin: "0 0 3px",
          }}
        >
          {label}
        </p>
        <p
          style={{
            fontSize: 22,
            fontWeight: 600,
            color: "#1a1a1a",
            margin: "0 0 4px",
            lineHeight: 1,
          }}
        >
          {value}
        </p>
        {badge && (
          <span
            style={{
              fontSize: 11,
              padding: "2px 7px",
              borderRadius: 99,
              background: cfg.badgeBg,
              color: cfg.badgeText,
              fontWeight: 500,
            }}
          >
            {badge}
          </span>
        )}
      </div>
    </div>
  );
}

function TagGroup({ items = [], maxVisible = null, alignLeft = false }) {
  const visible = maxVisible ? items.slice(0, maxVisible) : items;
  const hidden  = maxVisible ? items.length - maxVisible : 0;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: alignLeft ? "flex-start" : "flex-end" }}>
      {visible.map((item, i) => (
        <span
          key={i}
          style={{
            fontSize: 11,
            padding: "2px 7px",
            background: "#f0f0f0",
            color: "#555",
            borderRadius: 6,
            border: "0.5px solid #e0e0e0",
          }}
        >
          {item}
        </span>
      ))}
      {hidden > 0 && (
        <span
          style={{
            fontSize: 11,
            padding: "2px 7px",
            background: "#e8f0fe",
            color: "#185FA5",
            borderRadius: 6,
            border: "0.5px solid #c5d8f8",
            fontWeight: 500,
          }}
        >
          +{hidden} more
        </span>
      )}
    </div>
  );
}

function FieldRow({ label, children, expanded = false }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: expanded ? "flex-start" : "space-between",
        alignItems: "flex-start",
        gap: 8,
        fontSize: 13,
      }}
    >
      <span style={{ color: "#888", flexShrink: 0, minWidth: expanded ? 110 : "auto" }}>{label}</span>
      <div style={{ textAlign: expanded ? "left" : "right", flex: expanded ? 1 : "unset" }}>
        {children}
      </div>
    </div>
  );
}

function hasMoreData(member) {
  return (
    (member.role || []).length > 2 ||
    (member.documentType || []).length > 2 ||
    (member.documentCategory || []).length > 2
  );
}

function MemberCard({ member, onViewMore }) {
  const cfg = getTypeConfig(member.userType);

  return (
    <div
      style={{
        background: "#fff",
        border: "0.5px solid #e5e5e5",
        borderRadius: 12,
        padding: "20px",
        transition: "border-color 0.15s, box-shadow 0.15s",
        cursor: "default",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        boxSizing: "border-box",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#bbb";
        e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.07)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#e5e5e5";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          marginBottom: 14,
          paddingBottom: 14,
          borderBottom: "0.5px solid #e5e5e5",
          flexShrink: 0,
        }}
      >
        <Avatar
          size={44}
          style={{ background: cfg.bg, color: cfg.text, fontWeight: 500, fontSize: 14, flexShrink: 0 }}
        >
          {getInitials(member.firstName, member.lastName)}
        </Avatar>

        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              fontWeight: 500,
              fontSize: 15,
              margin: 0,
              color: "#1a1a1a",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {member.firstName} {member.lastName}
          </p>
          <p
            style={{
              fontSize: 12,
              color: "#888",
              margin: "2px 0 6px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {member.username}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            <span
              style={{
                fontSize: 11,
                padding: "2px 8px",
                borderRadius: 99,
                background: cfg.bg,
                color: cfg.text,
                fontWeight: 500,
              }}
            >
              {member.userType}
            </span>
            <Badge
              status={member.active ? "success" : "error"}
              text={
                <span style={{ fontSize: 11, color: member.active ? "#3B6D11" : "#A32D2D" }}>
                  {member.active ? "Active" : "Inactive"}
                </span>
              }
            />
          </div>
        </div>
      </div>

      {/* Fields */}
      <div style={{ display: "flex", flexDirection: "column", gap: 9, flex: 1, overflow: "hidden" }}>
        <FieldRow label="Email">
          <span style={{ color: "#185FA5", fontSize: 12, wordBreak: "break-all" }}>
            <MailOutlined style={{ marginRight: 4, fontSize: 11 }} />
            {member.email}
          </span>
        </FieldRow>

        <FieldRow label="Role">
          <TagGroup items={member.role} maxVisible={2} />
        </FieldRow>

        <FieldRow label="Doc type">
          <TagGroup items={member.documentType} maxVisible={2} />
        </FieldRow>

        <FieldRow label="Doc category">
          <TagGroup items={member.documentCategory} maxVisible={2} />
        </FieldRow>
      </div>

      {/* View More footer */}
      {hasMoreData(member) && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "0.5px solid #e5e5e5", flexShrink: 0 }}>
          <button
            onClick={() => onViewMore(member)}
            style={{
              width: "100%",
              border: "0.5px solid #d0d0d0",
              borderRadius: 8,
              background: "#fafafa",
              color: "#555",
              fontSize: 12,
              fontWeight: 500,
              padding: "7px 0",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 5,
              transition: "background 0.15s, border-color 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f0f0f0";
              e.currentTarget.style.borderColor = "#bbb";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fafafa";
              e.currentTarget.style.borderColor = "#d0d0d0";
            }}
          >
            <EyeOutlined style={{ fontSize: 12 }} />
            View More
          </button>
        </div>
      )}
    </div>
  );
}

function MemberModal({ member, onClose }) {
  if (!member) return null;
  const cfg = getTypeConfig(member.userType);

  return (
    <Modal
      open={!!member}
      onCancel={onClose}
      footer={null}
      centered
      width="min(600px, 95vw)"
      styles={{
        body: {
          padding: 0,
          maxHeight: "80vh",
          overflowY: "auto",
          borderRadius: 12,
        },
        content: {
          borderRadius: 12,
          overflow: "hidden",
          padding: 0,
        },
        mask: { backdropFilter: "blur(2px)" },
      }}
      closeIcon={
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: 99,
            background: "#f0f0f0",
            color: "#555",
          }}
        >
          <CloseOutlined style={{ fontSize: 12 }} />
        </span>
      }
    >
      {/* Modal header band */}
      <div
        style={{
          background: cfg.bg,
          padding: "24px 24px 20px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          borderBottom: "0.5px solid #e5e5e5",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Avatar size={54} style={{ background: "#fff", color: cfg.text, fontWeight: 600, fontSize: 16, flexShrink: 0, border: `1.5px solid ${cfg.text}22` }}>
          {getInitials(member.firstName, member.lastName)}
        </Avatar>
        <div>
          <p style={{ fontWeight: 600, fontSize: 17, margin: 0, color: "#1a1a1a" }}>
            {member.firstName} {member.lastName}
          </p>
          <p style={{ fontSize: 13, color: "#888", margin: "2px 0 6px" }}>{member.username}</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 12, padding: "2px 10px", borderRadius: 99, background: "#fff", color: cfg.text, fontWeight: 600, border: `1px solid ${cfg.text}33` }}>
              {member.userType}
            </span>
            <Badge
              status={member.active ? "success" : "error"}
              text={
                <span style={{ fontSize: 12, color: member.active ? "#3B6D11" : "#A32D2D" }}>
                  {member.active ? "Active" : "Inactive"}
                </span>
              }
            />
          </div>
        </div>
      </div>

      {/* Modal body — all rows use expanded=true so content aligns left */}
      <div style={{ padding: "20px 24px 28px", display: "flex", flexDirection: "column", gap: 18 }}>

        {/* Email */}
        <Section title="Email">
          <FieldRow label="Address" expanded>
            <span style={{ color: "#185FA5", fontSize: 13 }}>
              <MailOutlined style={{ marginRight: 5, fontSize: 12 }} />
              {member.email}
            </span>
          </FieldRow>
        </Section>

        {/* Roles */}
        {(member.role || []).length > 0 && (
          <Section title="Roles">
            <TagGroup items={member.role} alignLeft />
          </Section>
        )}

        {/* Document Types */}
        {(member.documentType || []).length > 0 && (
          <Section title="Document Types">
            <TagGroup items={member.documentType} alignLeft />
          </Section>
        )}

        {/* Document Categories */}
        {(member.documentCategory || []).length > 0 && (
          <Section title="Document Categories">
            <TagGroup items={member.documentCategory} alignLeft />
          </Section>
        )}
      </div>
    </Modal>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p style={{ fontSize: 11, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 8px", fontWeight: 600 }}>
        {title}
      </p>
      {children}
    </div>
  );
}

export default function ProjectMembers() {
  const [members, setMembers]         = useState([]);
  const [filtered, setFiltered]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [search, setSearch]           = useState("");
  const [typeFilter, setTypeFilter]   = useState("");
  const [modalMember, setModalMember] = useState(null);
  const projectName = JSON.parse(localStorage.getItem('Project'));

  useEffect(() => {
    if (!projectName) {
      setError("No project selected.");
      setLoading(false);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        const res = await axios.post("/get-project-member", { projectName });
        const data = res.data;
        const list = (Array.isArray(data.userDetails)
            ? data.userDetails
            : data.userDetails
            ? [data.userDetails]
            : []
        ).map((user) => ({
            ...user,
            userType: user.userType || "Super User"
        }));
        setMembers(list);
        setFiltered(list);
      } catch (err) {
        setError(err.message || "Failed to load members.");
      } finally {
        setLoading(false);
      }
    })();
  }, [projectName]);

  const applyFilter = useCallback(
    (q, type) => {
      const q2 = q.toLowerCase();
      setFiltered(
        members.filter((m) => {
          if (type && m.userType !== type) return false;
          if (q2) {
            const hay = `${m.firstName} ${m.lastName} ${m.username} ${m.email} ${(m.role || []).join(" ")}`.toLowerCase();
            if (!hay.includes(q2)) return false;
          }
          return true;
        })
      );
    },
    [members]
  );

  const handleSearch = (e) => {
    setSearch(e.target.value);
    applyFilter(e.target.value, typeFilter);
  };

  const handleTypeChange = (val) => {
    setTypeFilter(val);
    applyFilter(search, val);
  };

  const typeCounts = filtered.reduce((acc, m) => {
    acc[m.userType] = (acc[m.userType] || 0) + 1;
    return acc;
  }, {});

  const activeCount = filtered.filter((m) => m.active).length;

  return (
    <div style={{ padding: "24px", maxWidth: 1100, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 500, margin: 0, color: "#1a1a1a" }}>
            Project Members
          </h1>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Input
            prefix={<SearchOutlined style={{ color: "#aaa" }} />}
            placeholder="Search members…"
            value={search}
            onChange={handleSearch}
            style={{ width: 200 }}
            allowClear
          />
        </div>
      </div>

      {/* ── Stat bar ── */}
        {!loading && !error && filtered.length > 0 && (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
                marginBottom: 20,
            }}
        >
            {/* Total */}
            <StatCard
                label="Total Members"
                value={filtered.length}
                colorClass="total"
            />

            {/* Active */}
            <StatCard
                label="Active"
                value={activeCount}
                colorClass="active"
                badge={`${Math.round((activeCount / filtered.length) * 100)}% of total`}
            />

            {/* Inactive */}
            <StatCard
                label="Inactive"
                value={filtered.length - activeCount}
                colorClass="inactive"
                badge={`${Math.round(((filtered.length - activeCount) / filtered.length) * 100)}% of total`}
            />

            {/* Per-type cards */}
            {Object.entries(typeCounts).map(([type, count]) => (
            <StatCard
                key={type}
                label={`${type}s`}
                value={count}
                colorClass={type}
                badge={type}
            />
            ))}
        </div>
      )}

      {/* States */}
      {loading && (
        <div style={{ textAlign: "center", padding: "3rem" }}>
          <Spin size="large" />
          <p style={{ marginTop: 12, color: "#888" }}>Loading members…</p>
        </div>
      )}

      {!loading && error && (
        <Empty description={<span style={{ color: "#e24b4a" }}>{error}</span>} />
      )}

      {!loading && !error && filtered.length === 0 && (
        <Empty description="No members found for this project." />
      )}

      {/* Member grid */}
      {!loading && !error && filtered.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gridAutoRows: "1fr",
            gap: 12,
            alignItems: "stretch",
          }}
        >
          {filtered.map((m) => (
            <MemberCard
              key={m._id?.$oid || m._id || m.username}
              member={m}
              onViewMore={setModalMember}
            />
          ))}
        </div>
      )}

      {/* Detail modal */}
      <MemberModal member={modalMember} onClose={() => setModalMember(null)} />
    </div>
  );
}